/**
 * The trip's figures, worked out once for the summary header:
 *
 *   3 stops · 2 days · 1 night · 1,062 km · 12 h 40 m · $370 fuel
 *
 * Pure, so the numbers (and the fuel guess) are testable without a browser.
 * The fuel figure is a guide, never a quote: litres per 100 km for the rig
 * (your saved vehicle's own figure when it has one) times a typical pump
 * price, rounded to the nearest $10 so it doesn't pretend to be exact.
 */

export interface FuelProfile {
  /** Litres per 100 km. */
  l100km: number;
  /** Dollars per litre. */
  pricePerL: number;
  fuel: 'petrol' | 'diesel';
  /** How the rig is described in the explainer ("a caravan in tow"). */
  describe: string;
}

// Typical Australian figures, late 2026. Towing roughly doubles a car's use.
export const FUEL_PROFILES: Record<string, FuelProfile> = {
  car: { l100km: 8.5, pricePerL: 1.95, fuel: 'petrol', describe: 'a car' },
  '4wd': { l100km: 12.5, pricePerL: 2.05, fuel: 'diesel', describe: 'a 4WD' },
  caravan: { l100km: 17, pricePerL: 2.05, fuel: 'diesel', describe: 'a caravan in tow' },
  motorhome: { l100km: 14, pricePerL: 2.05, fuel: 'diesel', describe: 'a motorhome' },
};

export function fuelProfileFor(vehicleType: string | null | undefined, ownL100km?: number | null): FuelProfile {
  const key = vehicleType === 'campervan' ? 'motorhome' : vehicleType === 'sedan' ? 'car' : vehicleType || 'car';
  const base = FUEL_PROFILES[key] ?? FUEL_PROFILES.car;
  const own = typeof ownL100km === 'number' && Number.isFinite(ownL100km) && ownL100km > 2 && ownL100km < 60;
  return own ? { ...base, l100km: ownL100km as number } : base;
}

/** Whole dollars, rounded to the nearest $10 (nearest $1 under $20). */
export function estimateFuelCost(distanceKm: number | undefined, profile: FuelProfile): number | null {
  if (typeof distanceKm !== 'number' || !Number.isFinite(distanceKm) || distanceKm <= 0) return null;
  const dollars = (distanceKm / 100) * profile.l100km * profile.pricePerL;
  if (dollars < 20) return Math.max(1, Math.round(dollars));
  return Math.round(dollars / 10) * 10;
}

/** "About 17 L/100 km with a caravan in tow, at $2.05/L diesel. A guide, not a quote." */
export function fuelExplainer(profile: FuelProfile, own = false): string {
  const l = Number.isInteger(profile.l100km) ? String(profile.l100km) : profile.l100km.toFixed(1);
  return `${own ? 'Your vehicle’s' : 'About'} ${l} L/100 km${own ? '' : ` with ${profile.describe}`}, at $${profile.pricePerL.toFixed(2)}/L ${profile.fuel}. A guide, not a quote.`;
}

/** "12 h 40 m" (spaced, for the stats strip). "45 m" under the hour. */
export function driveParts(minutes?: number): { h: number; m: number } | null {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes <= 0) return null;
  const total = Math.round(minutes);
  return { h: Math.floor(total / 60), m: total % 60 };
}

export interface TripStatsInput {
  /** Distinct places (a round trip that hasn't left home counts once). */
  stopCount: number;
  nights: number;
  distanceKm?: number;
  driveMinutes?: number;
  vehicleType: string;
  /** The saved vehicle's own L/100 km, when it is the rig being planned. */
  ownL100km?: number | null;
  /** Straight-line figures (guests): the km and hours carry "≈". */
  estimate: boolean;
}

/**
 * "2 days · 1 night" — the trip's length, said the same way everywhere (the
 * header's counts line and the share card). A day trip is "1 day". Pure.
 */
export function daysNightsLabel(nights: number): string {
  const n = Math.max(0, Math.round(Number.isFinite(nights) ? nights : 0));
  const days = n + 1;
  const d = `${days} ${days === 1 ? 'day' : 'days'}`;
  return n > 0 ? `${d} · ${n} ${n === 1 ? 'night' : 'nights'}` : d;
}

export interface LongDaySummary {
  /** How many days drive longer than the limit. */
  count: number;
  /** The limit, in hours. */
  limitH: number;
  /** The stop above the first long day's longest drive (where a night would split it). */
  firstLeg?: number;
  /** "1 day over your 6h limit" · "2 days over your 6h limit". */
  text: string;
}

/**
 * The plan's biggest problem, for the header: how many days run over the
 * driver's daily limit. Null when none do (or the figures aren't in yet).
 * Pure: takes the day totals, so it doesn't care how they were worked out.
 */
export function longDaySummary(
  days: { minutes?: number; longestLeg?: number }[],
  maxDriveHours?: number
): LongDaySummary | null {
  const limitH = typeof maxDriveHours === 'number' && Number.isFinite(maxDriveHours) && maxDriveHours > 0 ? maxDriveHours : 8;
  const long = days.filter((d) => typeof d.minutes === 'number' && d.minutes > limitH * 60);
  if (!long.length) return null;
  const count = long.length;
  return {
    count,
    limitH,
    firstLeg: long.find((d) => typeof d.longestLeg === 'number')?.longestLeg,
    text: `${count} ${count === 1 ? 'day' : 'days'} over your ${limitH}h limit`,
  };
}

export interface TripStat {
  key: 'stops' | 'days' | 'nights' | 'km' | 'drive' | 'fuel';
  /** The figure ("1,062", "$370", "12"). */
  value: string;
  /** Its unit, in muted type after it ("km", "fuel"). */
  unit: string;
  /** For drive time: the minutes part ("40" + "m"). */
  extra?: { value: string; unit: string };
  /** Plain-English name for screen readers and the tooltip. */
  label: string;
  approx?: boolean;
  /** How a guessed figure is worked out (fuel). */
  note?: string;
}

/** The strip's figures, in order. Missing figures (no route yet) are skipped. */
export function tripStats(input: TripStatsInput): TripStat[] {
  const { stopCount, nights, distanceKm, driveMinutes, vehicleType, ownL100km, estimate } = input;
  const stats: TripStat[] = [
    {
      key: 'stops',
      value: String(stopCount),
      unit: stopCount === 1 ? 'stop' : 'stops',
      label: `${stopCount} ${stopCount === 1 ? 'stop' : 'stops'}`,
    },
    {
      key: 'days',
      value: String(Math.max(0, nights) + 1),
      unit: nights === 0 ? 'day' : 'days',
      label: `${Math.max(0, nights) + 1} ${nights === 0 ? 'day' : 'days'} on the road`,
    },
    {
      key: 'nights',
      value: String(nights),
      unit: nights === 1 ? 'night' : 'nights',
      label: nights === 0 ? 'No nights booked in yet' : `${nights} ${nights === 1 ? 'night' : 'nights'} away`,
    },
  ];
  if (typeof distanceKm === 'number' && Number.isFinite(distanceKm) && distanceKm > 0) {
    const km = distanceKm >= 100 ? Math.round(distanceKm) : Math.round(distanceKm * 10) / 10;
    stats.push({
      key: 'km',
      value: km.toLocaleString('en-AU'),
      unit: 'km',
      label: `${estimate ? 'About ' : ''}${km.toLocaleString('en-AU')} km ${estimate ? 'in a straight line' : 'of road'}`,
      approx: estimate,
    });
  }
  const d = driveParts(driveMinutes);
  if (d) {
    stats.push({
      key: 'drive',
      // Under the hour it is just minutes ("45 m"), never "0 h 45 m".
      value: String(d.h || d.m),
      unit: d.h ? 'h' : 'm',
      extra: d.h && d.m ? { value: String(d.m), unit: 'm' } : undefined,
      label: `${estimate ? 'About ' : ''}${d.h ? `${d.h} hours` : ''}${d.h && d.m ? ' ' : ''}${d.m ? `${d.m} minutes` : ''} of driving`,
      approx: estimate,
    });
  }
  const profile = fuelProfileFor(vehicleType, ownL100km);
  const cost = estimateFuelCost(distanceKm, profile);
  if (cost !== null) {
    stats.push({
      key: 'fuel',
      value: `$${cost.toLocaleString('en-AU')}`,
      unit: 'fuel',
      label: `About $${cost.toLocaleString('en-AU')} of fuel.`,
      note: fuelExplainer(profile, profile.l100km !== fuelProfileFor(vehicleType).l100km),
      approx: true,
    });
  }
  return stats;
}
